/**
 * VipBookingWidget
 * ────────────────
 * Live availability widget for the HustleStadium sales page. Renders
 * an in-page picker that fetches `/api/stadium/vip-availability` and
 * lets the visitor lock in a table + submit an inquiry via
 * `/api/stadium/vip-inquire`.
 *
 * Anonymous — no login required. Backend seeds mock venues so the
 * sales page has something real to show before HustlePlate Table
 * Builder feeds are wired up per venue.
 */
import { useEffect, useMemo, useState } from "react";
import { Award, ArrowRight, MapPin, Check } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL
  || (typeof window !== "undefined" ? window.location.origin : "");

const ZONE_ICON = { restaurant: "🍽️", box: "🥂", "viewing-deck": "🏟️" };

export default function VipBookingWidget() {
  const [venues, setVenues] = useState([]);
  const [venueSlug, setVenueSlug] = useState("suncorp-stadium");
  const [eventDate, setEventDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 14);
    return d.toISOString().slice(0, 10);
  });
  const [layout, setLayout] = useState(null);
  const [selectedZone, setSelectedZone] = useState(null);
  const [selectedTable, setSelectedTable] = useState(null);
  const [partySize, setPartySize] = useState(6);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  // Season-pass lock — only shown when the venue's ops team enabled it.
  const [seasonLock, setSeasonLock] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/stadium/venues`)
      .then((r) => r.ok ? r.json() : { items: [] })
      .then((j) => setVenues(j.items || []))
      .catch(() => setVenues([]));
  }, []);

  useEffect(() => {
    setLayout(null);
    setSelectedZone(null);
    setSelectedTable(null);
    fetch(`${API}/api/stadium/vip-availability?venue=${encodeURIComponent(venueSlug)}&date=${encodeURIComponent(eventDate)}`)
      .then((r) => r.ok ? r.json() : null)
      .then((j) => {
        if (!j) return;
        setLayout(j);
        const firstZone = (j.zones || [])[0];
        if (firstZone) setSelectedZone(firstZone.zone_id);
      })
      .catch(() => setLayout(null));
  }, [venueSlug, eventDate]);

  const zoneObj = useMemo(
    () => (layout?.zones || []).find((z) => z.zone_id === selectedZone) || null,
    [layout, selectedZone],
  );

  const submit = async () => {
    if (!selectedTable || !name || !email) {
      toast.error("Pick a table and share your name + email.");
      return;
    }
    setBusy(true);
    try {
      if (seasonLock) {
        // Season-pass VIP lock — separate endpoint that reserves the
        // table for the entire season on venues that allow it.
        const res = await fetch(`${API}/api/stadium/season-lock`, {
          method:  "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            venue_slug:   venueSlug,
            table_no:     selectedTable,
            zone_id:      selectedZone,
            season:       `${new Date(eventDate).getFullYear()}`,
            home_dates:   [eventDate],
            holder_name:  name,
            holder_email: email,
            party_size:   partySize,
          }),
        });
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error(j.detail || `HTTP ${res.status}`);
        }
        const j = await res.json().catch(() => ({}));
        if (j.checkout_url) {
          // Redirect the fan straight to Stripe Checkout for the
          // deposit. Per-match instalments are billed by the venue.
          toast.success("Redirecting to Stripe to lock in your deposit…");
          setTimeout(() => { window.location.href = j.checkout_url; }, 600);
          return;
        }
        setSent(true);
        toast.success("Season-pass VIP lock request sent — venue ops will confirm shortly.");
        return;
      }
      const res = await fetch(`${API}/api/stadium/vip-inquire`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          venue_slug: venueSlug,
          event_date: eventDate,
          zone_id: selectedZone,
          table_no: selectedTable,
          party_size: partySize,
          name, email, phone,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSent(true);
      toast.success("VIP inquiry received — we'll be in touch within 4 business hours.");
    } catch (e) {
      toast.error(`Could not send inquiry: ${e.message}`);
    }
    setBusy(false);
  };

  return (
    <div data-testid="vip-booking-widget"
         style={{ background: "#fff", border: "1px solid #ecdeb7",
                    borderRadius: 18, padding: "26px 26px",
                    boxShadow: "0 20px 44px rgba(0,0,0,.08)" }}>
      <div style={{ display: "flex", gap: 12, alignItems: "center",
                     marginBottom: 6, flexWrap: "wrap" }}>
        <Award size={22} color="#b08a3e" />
        <h3 style={{ margin: 0, fontFamily: "Georgia,serif", fontSize: 22, color: "#1a1408" }}>
          Book a VIP room — live availability
        </h3>
      </div>
      <p style={{ margin: "0 0 18px", color: "#5a4a1a", fontSize: 14, lineHeight: 1.55 }}>
        Wired straight into HustlePlate's Table Builder. Pick a venue,
        a date and a table — every seat inherits a real block-row-seat
        identifier so runners deliver food to the exact chair.
      </p>

      {/* Filters */}
      <div data-testid="vip-widget-filters"
           style={{ display: "grid", gap: 12,
                     gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                     marginBottom: 18 }}>
        <label style={{ fontSize: 12, color: "#3a2f18", fontWeight: 600, display: "block" }}>
          Venue
          <select data-testid="vip-venue-select"
                    value={venueSlug} onChange={(e) => setVenueSlug(e.target.value)}
                    style={inputStyle}>
            {venues.length === 0
              ? <option value={venueSlug}>Loading…</option>
              : venues.map((v) => (
                  <option key={v.slug} value={v.slug}>{v.label}</option>
                ))}
          </select>
        </label>
        <label style={{ fontSize: 12, color: "#3a2f18", fontWeight: 600, display: "block" }}>
          Event date
          <input data-testid="vip-date-input"
                   type="date" value={eventDate}
                   onChange={(e) => setEventDate(e.target.value)}
                   style={inputStyle} />
        </label>
        <label style={{ fontSize: 12, color: "#3a2f18", fontWeight: 600, display: "block" }}>
          Party size
          <input data-testid="vip-party-size"
                   type="number" min="1" max="200"
                   value={partySize}
                   onChange={(e) => setPartySize(parseInt(e.target.value || "1", 10))}
                   style={inputStyle} />
        </label>
      </div>

      {/* Zones */}
      {layout && (
        <div data-testid="vip-zones-strip"
             style={{ display: "flex", gap: 8, flexWrap: "wrap",
                       marginBottom: 12 }}>
          {(layout.zones || []).map((z) => {
            const active = z.zone_id === selectedZone;
            return (
              <button key={z.zone_id}
                        data-testid={`vip-zone-${z.zone_id}`}
                        onClick={() => { setSelectedZone(z.zone_id); setSelectedTable(null); }}
                        style={{
                          border: active ? "2px solid #b08a3e" : "1px solid #ecdeb7",
                          background: active ? "#fff8e6" : "#fff",
                          padding: "8px 14px", borderRadius: 999,
                          cursor: "pointer", fontSize: 13,
                          color: "#1a1408", fontWeight: 600,
                        }}>
                <span style={{ marginRight: 6 }}>{ZONE_ICON[z.type] || "🎟️"}</span>
                {z.zone_name}
              </button>
            );
          })}
        </div>
      )}

      {/* Tables */}
      {zoneObj && (
        <div data-testid="vip-tables-grid"
             style={{ display: "grid",
                       gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))",
                       gap: 10, marginBottom: 16 }}>
          {(zoneObj.tables || []).map((t) => {
            const active = t.table_no === selectedTable;
            const disabled = !t.available;
            return (
              <button key={t.table_no}
                        data-testid={`vip-table-${t.table_no}`}
                        disabled={disabled}
                        onClick={() => setSelectedTable(t.table_no)}
                        style={{
                          border: active ? "2px solid #b08a3e" : "1px solid #ecdeb7",
                          background: disabled ? "#f5efe0" : (active ? "#fff8e6" : "#fff"),
                          color: disabled ? "#a49577" : "#1a1408",
                          padding: "12px 14px", borderRadius: 12,
                          textAlign: "left", cursor: disabled ? "not-allowed" : "pointer",
                          opacity: disabled ? 0.7 : 1,
                        }}>
                <div style={{ fontSize: 13, fontWeight: 700, display: "flex",
                                alignItems: "center", gap: 6, marginBottom: 4 }}>
                  <MapPin size={13} color="#b08a3e" /> Table {t.table_no}
                </div>
                <div style={{ fontSize: 11, color: "#5a4a1a", marginBottom: 6 }}>
                  Seats {t.seats_from} → {t.seats_to}
                </div>
                <div style={{ fontSize: 12, fontWeight: 600,
                                color: disabled ? "#a49577" : "#c2410c" }}>
                  {disabled ? "Booked" : `From $${t.price_pp} pp`}
                </div>
                {active && !disabled && (
                  <div style={{ marginTop: 6, fontSize: 11, color: "#1f7a1f",
                                  fontWeight: 700, display: "flex", alignItems: "center", gap: 4 }}>
                    <Check size={12} /> Selected
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Inquiry form */}
      <div data-testid="vip-inquiry-form"
           style={{ display: "grid", gap: 10,
                     gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                     marginTop: 6 }}>
        <input data-testid="vip-name" placeholder="Your name"
                 value={name} onChange={(e) => setName(e.target.value)}
                 style={inputStyle} />
        <input data-testid="vip-email" placeholder="Email"
                 value={email} onChange={(e) => setEmail(e.target.value)}
                 type="email" style={inputStyle} />
        <input data-testid="vip-phone" placeholder="Phone (optional)"
                 value={phone} onChange={(e) => setPhone(e.target.value)}
                 style={inputStyle} />
      </div>

      {/* Season-pass VIP lock — only when the venue has enabled it in Ops Settings */}
      {layout?.allow_season_lock && (
        <label data-testid="vip-season-lock-toggle"
               style={{ marginTop: 12, display: "flex", alignItems: "center",
                          gap: 8, background: "#fff8e6",
                          border: "1px solid #ecdeb7", borderRadius: 10,
                          padding: "10px 12px", cursor: "pointer",
                          fontSize: 13, color: "#3a2f18" }}>
          <input type="checkbox"
                   checked={seasonLock}
                   onChange={(e) => setSeasonLock(e.target.checked)} />
          <span>
            <strong>Lock this table for the whole season.</strong>
            {" "}Reserves the same table + seats across every home game — perfect for corporate memberships and season-ticket holders.
          </span>
        </label>
      )}

      <div style={{ marginTop: 16, display: "flex", gap: 10, flexWrap: "wrap",
                     alignItems: "center" }}>
        <button data-testid="vip-submit-btn"
                  onClick={submit} disabled={busy || sent}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 8,
                    padding: "12px 20px", borderRadius: 999, border: 0,
                    background: sent ? "#1f7a1f" : "#c2410c",
                    color: "#fff", fontWeight: 700, fontSize: 14,
                    cursor: (busy || sent) ? "not-allowed" : "pointer",
                    boxShadow: "0 8px 20px rgba(194,65,12,.22)",
                  }}>
          {sent ? "Request sent ✓"
                : busy ? "Sending…"
                : seasonLock
                  ? <>Lock this table for the season <ArrowRight size={15} /></>
                  : <>Request this table <ArrowRight size={15} /></>}
        </button>
        {selectedTable && !sent && (
          <div style={{ fontSize: 12, color: "#5a4a1a" }}>
            Locking in{" "}
            <strong>Table {selectedTable}</strong> at{" "}
            <strong>{layout?.venue_label}</strong> on{" "}
            <strong>{eventDate}</strong> · party of {partySize}.
          </div>
        )}
      </div>
    </div>
  );
}

const inputStyle = {
  width: "100%", padding: "9px 12px", marginTop: 4,
  border: "1px solid #ecdeb7", borderRadius: 8,
  fontSize: 13, background: "#fff", color: "#1a1408",
  fontFamily: "inherit",
};
